import type {
  APICrmReference,
  APICrmTask,
  CrmSection,
  CrmTaskCreateRequest,
  CrmTaskUpdateRequest,
} from '@/services/crmService'

type TaskDraft = Partial<APICrmTask>

/** A new task needs a type and an assignee; its meter is optional. */
export const buildCreateRequest = (
  draft: TaskDraft,
  description: string,
  comment: string
): CrmTaskCreateRequest | null => {
  if (draft.typeId == null || draft.assigneeId == null) return null
  const meter = (draft.meterNumber ?? '').trim()
  return {
    typeId: draft.typeId,
    meterNumber: meter || undefined,
    assigneeId: draft.assigneeId,
    statusId: draft.statusId ?? undefined,
    priority: draft.priority ?? undefined,
    dueAt: draft.dueAt ?? undefined,
    description: description.trim() || undefined,
    comment: comment.trim() || undefined,
  }
}

const HEADER_FIELDS = ['typeId', 'assigneeId', 'statusId', 'priority'] as const

/** Only what differs from the loaded task. A blank meter number removes the meter. */
export const buildUpdatePatch = (
  task: APICrmTask,
  draft: TaskDraft,
  description: string,
  comment: string
): CrmTaskUpdateRequest => {
  const patch: CrmTaskUpdateRequest = {}
  for (const field of HEADER_FIELDS) {
    const value = draft[field]
    if (value != null && value !== task[field]) {
      Object.assign(patch, { [field]: value })
    }
  }
  const meter = (draft.meterNumber ?? '').trim()
  if (meter !== (task.meterNumber ?? '')) patch.meterNumber = meter
  if ((draft.dueAt ?? null) !== (task.dueAt ?? null)) {
    if (draft.dueAt) patch.dueAt = draft.dueAt
    else patch.clearDueAt = true
  }
  if (description !== (task.description ?? '')) patch.description = description
  if (comment.trim()) patch.comment = comment.trim()
  return patch
}

/** The status a task takes when it is moved to a section; the default `to_do` for open. */
export const statusForSection = (
  reference: APICrmReference | undefined,
  section: CrmSection
): number | undefined => {
  const inSection = reference?.statuses.filter(s => s.section === section)
  return (inSection?.find(s => s.key === 'to_do') ?? inSection?.[0])?.id
}
